'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Medal } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';

// "Izazov nedelje" (2026-09-05): best realized R on gold this week, live standings from the
// settlement engine, resets every Monday. Prize: 15 min 1-on-1 with FX Doctor (owner, 2026-09-24).
// Gives a reason to post a signal THIS week rather than "some time".

type Row = { rank: number; username: string; displayName: string; image: string | null; realizedR: number; direction: string; tradeId: string | null };
type Data = { weekStart: string; weekEnd: string; standings: Row[]; closedThisWeek: number; lastWeekWinner?: { username: string; displayName: string; realizedR: number; tradeId: string | null } | null };


export default function WeeklyChallengeCard({ onPostTrade }: { onPostTrade: () => void }) {
  const { t } = useLang();
  const [data, setData] = useState<Data | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/challenge/weekly').then((r) => (r.ok ? r.json() : null)).then((d) => { if (!cancelled && d) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const end = new Date(data.weekEnd);
  const daysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / 864e5));
  const endLabel = new Intl.DateTimeFormat('sr-Latn-RS', { timeZone: 'Europe/Belgrade', day: 'numeric', month: 'numeric' }).format(new Date(end.getTime() - 1));

  return (
    <section className="rounded-2xl p-4 sm:p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(28,43,38,0.06)' }} aria-label={t('Challenge of the week')}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Medal size={16} style={{ color: 'var(--gold, #C9A227)' }} />
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('Challenge of the week')}: {t('best R on gold')}</h2>
        </div>
        <span className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>{daysLeft} {t('days left')} · {t('until')} {endLabel}</span>
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
        {t('Post an XAUUSD signal; the market closes it; the highest realized R by Sunday wins')}{' '}
        <span className="font-semibold" style={{ color: 'var(--accent)' }}>{t('15 minutes 1-on-1 with FX Doctor')}</span>.
      </p>
      {data.lastWeekWinner && (
        <p className="text-xs mb-3 rounded-lg px-3 py-2" style={{ background: 'var(--win-muted)', color: 'var(--text-secondary)' }}>
          🏆 {t('Last week’s winner')}:{' '}
          <Link href={data.lastWeekWinner.tradeId ? `/ideas/${data.lastWeekWinner.tradeId}` : `/profile/${data.lastWeekWinner.username}`} className="font-bold" style={{ color: 'var(--text-primary)' }}>@{data.lastWeekWinner.username}</Link>
          {' '}<span className="font-mono-nums font-bold" style={{ color: 'var(--win)' }}>+{data.lastWeekWinner.realizedR.toFixed(2)} R</span>
        </p>
      )}

      {data.standings.length === 0 ? (
        <div className="text-sm rounded-xl px-3 py-2.5" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)' }}>
          {t('No gold signal has been closed by the market this week yet — the first one leads.')}
        </div>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {data.standings.map((r) => (
            <li key={r.username} className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: r.rank === 1 ? 'var(--accent-muted)' : 'var(--bg-sunken)' }}>
              <span className="font-mono-nums font-bold text-sm w-5" style={{ color: r.rank === 1 ? 'var(--accent)' : 'var(--text-muted)' }}>{r.rank}</span>
              <div className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
                {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" /> : r.displayName[0]?.toUpperCase()}
              </div>
              <Link href={`/profile/${r.username}`} className="text-sm font-semibold truncate flex-1" style={{ color: 'var(--text-primary)' }}>{r.displayName}</Link>
              <span className="font-mono-nums font-bold text-sm" style={{ color: r.realizedR >= 0 ? 'var(--win)' : 'var(--bearish)' }}>
                {r.realizedR >= 0 ? '+' : ''}{r.realizedR.toFixed(2)} R
              </span>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{data.closedThisWeek} {t('gold signals closed this week')}</span>
        <button onClick={onPostTrade} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
          {t('Enter with a signal')}
        </button>
      </div>
    </section>
  );
}
